/**
 * Client runtime.
 *
 * The only place in the web application that performs a network call. A lint
 * rule forbids raw fetch everywhere else, so every request the product makes
 * passes through one place with one error model and one auth policy.
 */

import type { ErrorBody as ApiErrorBody, FieldProblem } from './generated/types.js';
import { isQueueable, MAX_QUEUED_WRITES, type OfflineStore, type QueuedWrite } from './offline.js';

export type { ApiErrorBody, FieldProblem };

/** The header the server reads. One spelling, shared by both sides. */
export const IDEMPOTENCY_HEADER = 'idempotency-key';

/**
 * Thrown when a write could not be sent and was kept on this device instead.
 *
 * Deliberately an error and not a success. The work is real and it is durable,
 * but it is not in the workspace, and nobody else can see it - which is
 * precisely what a person needs to know before they hand the laptop over or
 * tell a colleague the account is updated. Screens that do nothing special
 * with it show their ordinary failure message, which is the right default: a
 * screen that said "saved" would be worse than one that said "could not save".
 */
export class QueuedOfflineError extends Error {
  readonly code = 'QUEUED_OFFLINE';
  readonly operationId: string;
  /** The queued write's id, which is also its idempotency key. */
  readonly writeId: string;

  constructor(operationId: string, writeId: string) {
    super('No network. This is saved on your device and will be sent when you are back online.');
    this.name = 'QueuedOfflineError';
    this.operationId = operationId;
    this.writeId = writeId;
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly rule?: string;
  readonly problems?: FieldProblem[];
  readonly nextStep?: { label: string; href?: string };
  readonly traceId?: string;
  readonly operationId: string;
  /**
   * The server's sentence as a catalogue key, when it has one (F-024).
   *
   * `message` is English. Where a key is present the screen should render that
   * instead, through `useT()`, which is the only place that knows the reader's
   * language. `errorMessage()` in the web app does exactly that and falls back
   * to the English, so a message nobody has converted still reads.
   */
  readonly messageKey?: string;
  readonly messageValues?: Record<string, string>;

  constructor(status: number, body: ApiErrorBody, operationId: string) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.operationId = operationId;
    if (body.rule) this.rule = body.rule;
    if (body.problems) this.problems = body.problems;
    if (body.nextStep) this.nextStep = body.nextStep;
    if (body.traceId) this.traceId = body.traceId;
    if (body.messageKey) this.messageKey = body.messageKey;
    if (body.messageValues) this.messageValues = body.messageValues;
  }

  /** True when the caller is not allowed to know whether the record exists. */
  get isHidden(): boolean {
    return this.status === 404;
  }
}

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export interface ClientConfig {
  baseUrl: string;
  fetch: FetchLike;
  /** Sent as Authorization: Bearer when present. Cookies are used otherwise. */
  token: string | null;
  /** Extra headers applied to every request (used by the test harness). */
  headers: Record<string, string>;
  /**
   * Where writes go when there is no network. Null means they do not go
   * anywhere, and the failure reaches the caller unchanged.
   *
   * Injected rather than imported, so the node test harness and the server-side
   * uses of this client behave exactly as they did before offline existed. A
   * queue that installed itself would change what every existing test proves.
   */
  offlineStore: OfflineStore | null;
  /**
   * Names a write for the queue, in the words of whoever is making it.
   *
   * Set by the shell for the duration of one interaction. The queue panel
   * shows a person what is waiting, and "Priya Raman" is a thing they can
   * recognise three hours later in a different building.
   */
  describeWrite: (spec: RequestSpec) => string;
  /**
   * Told when a write has been kept, so the indicator can count it.
   *
   * A callback rather than the queue polling: the count must change in the
   * same moment the person is told their save did not reach the workspace, or
   * they read one message and see a bar that disagrees with it.
   */
  onQueued: (write: QueuedWrite) => void;
}

const config: ClientConfig = {
  baseUrl: '',
  fetch: (input, init) => globalThis.fetch(input, init),
  token: null,
  headers: {},
  offlineStore: null,
  describeWrite: (spec) => spec.operationId,
  onQueued: () => {},
};

export function configureClient(next: Partial<ClientConfig>): void {
  Object.assign(config, next);
}

export function clientConfig(): Readonly<ClientConfig> {
  return config;
}

export interface RequestOptions {
  signal?: AbortSignal;
  /** Overrides the ambient token for one call. */
  token?: string | null;
  headers?: Record<string, string>;
}

export interface RequestSpec {
  operationId: string;
  method: string;
  path: string;
  pathParams?: Record<string, string | number> | undefined;
  query?: Record<string, unknown> | undefined;
  body?: unknown;
}

function buildPath(template: string, params: Record<string, string | number> | undefined): string {
  return template.replace(/\{([A-Za-z0-9_]+)\}/g, (_all, name: string) => {
    const value = params?.[name];
    if (value === undefined || value === null || value === '') {
      throw new Error(`Missing path parameter '${name}' for ${template}.`);
    }
    return encodeURIComponent(String(value));
  });
}

function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    // An absent key and a key set to undefined mean the same thing here - the
    // caller did not ask. `null` is different: it is an explicit value and is
    // sent, so a filter for "no owner" is expressible.
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value) parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
    } else if (value === null) {
      parts.push(`${encodeURIComponent(key)}=`);
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

export async function request<T>(spec: RequestSpec, options: RequestOptions = {}): Promise<T> {
  const url = `${config.baseUrl}${buildPath(spec.path, spec.pathParams)}${buildQuery(spec.query)}`;
  const token = options.token !== undefined ? options.token : config.token;
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...config.headers,
    ...options.headers,
  };
  if (spec.body !== undefined) headers['content-type'] = 'application/json';
  if (token) headers['authorization'] = `Bearer ${token}`;

  const init: RequestInit = {
    method: spec.method,
    headers,
    credentials: 'include',
  };
  if (spec.body !== undefined) init.body = JSON.stringify(spec.body);
  if (options.signal) init.signal = options.signal;

  let response: Response;
  try {
    response = await config.fetch(url, init);
  } catch (error) {
    /*
     * No round trip happened, so nothing was decided.
     *
     * This is the only branch where keeping the write is honest. A response
     * with a bad status means the server considered the request and said no,
     * and replaying that on a timer would hide a real fault behind a queue
     * that never drains.
     */
    const kept = await keepForLater(spec, error);
    if (kept) throw kept;
    throw error;
  }

  const text = await response.text();
  const parsed: unknown = text ? safeJson(text) : null;

  if (!response.ok) {
    const body: ApiErrorBody =
      parsed && typeof parsed === 'object' && 'code' in parsed
        ? (parsed as ApiErrorBody)
        : {
            code: 'INTERNAL',
            message: `${spec.operationId} failed with status ${response.status}. Try again, and if it keeps failing report the time it happened.`,
          };
    throw new ApiError(response.status, body, spec.operationId);
  }
  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Keeps a write that could not be sent, or declines to.
 *
 * Declines in three cases, each for its own reason. No store configured: this
 * is the node harness or a server-side caller, and inventing a queue under
 * them would change what every existing test proves. Not a write: there is
 * nothing to replay, and a read served later from a queue would be a stale
 * answer wearing a fresh one's clothes. Not declared queueable: the route said
 * replaying it late is not the thing the person meant, and that declaration is
 * the server's to make, not this file's.
 */
async function keepForLater(spec: RequestSpec, cause: unknown): Promise<QueuedOfflineError | null> {
  const store = config.offlineStore;
  if (!store) return null;
  if (spec.method.toLowerCase() === 'get') return null;
  if (!isQueueable(spec.operationId)) return null;

  /*
   * A full queue refuses the new write rather than dropping an old one.
   *
   * Dropping the oldest would discard work somebody was already told was
   * kept, silently, and they would find out weeks later or never. Refusing
   * the newest tells them now, while they can still do something about it -
   * write it on paper, or go and find a signal.
   */
  try {
    const held = await store.all();
    if (held.length >= MAX_QUEUED_WRITES) return null;
  } catch {
    return null;
  }

  const id = crypto.randomUUID();
  const write: QueuedWrite = {
    id,
    queuedAt: new Date().toISOString(),
    operationId: spec.operationId,
    spec,
    label: config.describeWrite(spec),
    state: 'waiting',
    attempts: 0,
    response: null,
    refusal: null,
  };
  try {
    await store.put(write);
  } catch {
    /*
     * The store itself failed - private browsing, a full disk, a browser with
     * storage switched off. The original network error is the truthful thing
     * to report, because the write really is lost, and telling somebody it is
     * safe on their device when the device refused to take it is the one
     * outcome worse than failing.
     */
    return null;
  }
  void cause;
  config.onQueued(write);
  return new QueuedOfflineError(spec.operationId, id);
}
